import React, { useEffect, useState } from 'react';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import { Button, Input, Switch, Tag, Select, Space, message, Popconfirm, Modal, Form, InputNumber, TimePicker, Checkbox } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;

// Filters, search and paging live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  entityType: { type: 'string', key: 'entity_type' },
  triggerType: { type: 'string', key: 'trigger_type' },
  active: { type: 'boolean' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const ReminderRulesTable = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Filters
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  // Any filter change goes back to the first page.
  const urlSetter = (name) => (value) => setFilters({ [name]: value, page: 1 });
  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);
  const entityTypeFilter = filters.entityType || undefined;
  const setEntityTypeFilter = urlSetter('entityType');
  const triggerTypeFilter = filters.triggerType || undefined;
  const setTriggerTypeFilter = urlSetter('triggerType');
  const activeFilter = filters.active;
  const setActiveFilter = urlSetter('active');

  // Search sits outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount =
    (entityTypeFilter ? 1 : 0) +
    (triggerTypeFilter ? 1 : 0) +
    (activeFilter !== null && activeFilter !== undefined ? 1 : 0);

  const clearFilters = () => {
    setFilters({ entityType: null, triggerType: null, active: null, page: 1 });
  };

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();

  // Template state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  useEffect(() => {
    fetchReminderRules();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      // Fetch email templates
      const emailResponse = await axios.get('/template/', {
        params: {
          template_type: 'EMAIL',
          page: 1,
          page_size: 100
        }
      });
      if (emailResponse.data) {
        setEmailTemplates(emailResponse.data.results || emailResponse.data || []);
      }

      // Fetch WhatsApp templates (approved only)
      const whatsappResponse = await axios.get('/reminders/whatsapp-templates/', {
        params: { approval_status: 'APPROVED' }
      });
      if (whatsappResponse.data) {
        setWhatsappTemplates(whatsappResponse.data.results || whatsappResponse.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [dataSource, searchTerm, entityTypeFilter, triggerTypeFilter, activeFilter]);

  const fetchReminderRules = async () => {
    setLoading(true);
    try {
      // Paged client-side, so load up to the largest page size on offer.
      const response = await axios.get('/reminders/rules/', {
        params: {
          page_size: MAX_PAGE_SIZE
        }
      });
      if (response.status === 200) {
        setDataSource(response.data.results || response.data || []);
      }
    } catch (error) {
      messageApi.error('Failed to fetch reminder rules');
      console.error('Error fetching rules:', error);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...dataSource];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Entity type filter
    if (entityTypeFilter) {
      filtered = filtered.filter(item => item.entity_type === entityTypeFilter);
    }

    // Trigger type filter
    if (triggerTypeFilter) {
      filtered = filtered.filter(item => item.trigger_type === triggerTypeFilter);
    }

    // Active filter
    if (activeFilter !== null) {
      filtered = filtered.filter(item => item.is_active === activeFilter);
    }

    setFilteredData(filtered);
  };

  const handleToggleActive = async (record) => {
    try {
      const response = await axios.post(`/reminders/rules/${record.id}/toggle_active/`);
      if (response.status === 200) {
        messageApi.success(`Rule ${response.data.is_active ? 'activated' : 'deactivated'}`);
        fetchReminderRules();
      }
    } catch (error) {
      messageApi.error('Failed to toggle rule status');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/reminders/rules/${id}/`);
      if (response.status === 204) {
        messageApi.success('Rule deleted successfully');
        fetchReminderRules();
      }
    } catch (error) {
      messageApi.error('Failed to delete rule');
    }
  };

  const handleEdit = (record) => {
    setEditingRule(record);
    form.setFieldsValue({
      ...record,
      trigger_time: record.trigger_time ? dayjs(record.trigger_time, 'HH:mm:ss') : null,
    });
    setIsModalVisible(true);
  };

  const handleCreateNew = () => {
    setEditingRule(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        trigger_time: values.trigger_time ? values.trigger_time.format('HH:mm:ss') : '09:00:00',
      };

      let response;
      if (editingRule) {
        response = await axios.put(`/reminders/rules/${editingRule.id}/`, payload);
      } else {
        response = await axios.post('/reminders/rules/', payload);
      }

      if (response.status === 200 || response.status === 201) {
        messageApi.success(`Rule ${editingRule ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        form.resetFields();
        fetchReminderRules();
      }
    } catch (error) {
      messageApi.error('Failed to save rule');
      console.error('Error saving rule:', error);
    }
  };

  const getChannelTags = (record) => {
    const channels = [];
    if (record.send_email) channels.push(<Tag color="blue" key="email">Email</Tag>);
    if (record.send_whatsapp) channels.push(<Tag color="green" key="whatsapp">WhatsApp</Tag>);
    if (record.send_sms) channels.push(<Tag color="purple" key="sms">SMS</Tag>);
    return channels.length > 0 ? channels : <Tag>None</Tag>;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Entity Type',
      dataIndex: 'entity_type_display',
      key: 'entity_type',
      width: 120,
      render: (text) => <Tag color="cyan">{text}</Tag>,
    },
    {
      title: 'Trigger',
      key: 'trigger',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.trigger_type_display}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.trigger_days !== 0 ? `${record.trigger_days} days` : record.trigger_minutes ? `${record.trigger_minutes} min` : 'On date'}
          </div>
        </div>
      ),
    },
    {
      title: 'Channels',
      key: 'channels',
      width: 200,
      render: (_, record) => getChannelTags(record),
    },
    {
      title: 'Recipients',
      key: 'recipients',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          {record.notify_customer && <div>✓ Customer</div>}
          {record.notify_admin && <div>✓ Admin</div>}
          {record.notify_sales_rep && <div>✓ Sales Rep</div>}
        </div>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: 'Active',
      key: 'is_active',
      width: 80,
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          onChange={() => handleToggleActive(record)}
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{
        padding: '24px',
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Reminder Rules</h2>
        </div>

        <FilterBar
          searchValue={searchTermLive}
          onSearchChange={(value) => { setSearchTermLive(value); setSearchTerm(value); }}
          onSearch={setSearchTerm}
          searchPlaceholder="Search rules"
          activeCount={activeFilterCount}
          onClear={clearFilters}
          title="Filter Rules"
          addLabel="Create New Rule"
          onAdd={handleCreateNew}
          onRefresh={() => fetchReminderRules()}
          refreshing={loading}
        >
          <FilterField label="Entity Type">
            <Select
              placeholder="Entity Type"
              value={entityTypeFilter}
              onChange={setEntityTypeFilter}
              allowClear
            >
              <Option value="INVOICE">Invoice</Option>
              <Option value="QUOTE">Quote</Option>
              <Option value="LEAD">Lead</Option>
            </Select>
          </FilterField>

          <FilterField label="Trigger Type">
            <Select
              placeholder="Trigger Type"
              value={triggerTypeFilter}
              onChange={setTriggerTypeFilter}
              allowClear
            >
              <Option value="PRE_DUE">Before Due</Option>
              <Option value="DUE">On Due</Option>
              <Option value="OVERDUE">Overdue</Option>
              <Option value="PRE_EXPIRY">Before Expiry</Option>
              <Option value="EXPIRED">Expired</Option>
              <Option value="IMMEDIATE">Immediate</Option>
            </Select>
          </FilterField>

          <FilterField label="Status">
            <Select
              placeholder="Status"
              value={activeFilter}
              onChange={setActiveFilter}
              allowClear
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </FilterField>
        </FilterBar>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200, y: 'calc(100vh - 380px)' }}
          pagination={{
            current: filters.page,
            pageSize: Math.min(filters.pageSize, MAX_PAGE_SIZE),
            onChange: (page, size) => setFilters({ page: size !== filters.pageSize ? 1 : page, pageSize: size }),
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} rules`,
          }}
        />

        <Modal
          title={editingRule ? 'Edit Reminder Rule' : 'Create Reminder Rule'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
          }}
          width={800}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Rule Name"
              rules={[{ required: true, message: 'Please enter rule name' }]}
            >
              <Input placeholder="e.g., 3 Days Before Due - Professional Reminder" />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} placeholder="Brief description of this rule" />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Form.Item
                name="entity_type"
                label="Entity Type"
                rules={[{ required: true, message: 'Please select entity type' }]}
              >
                <Select placeholder="Select entity type">
                  <Option value="INVOICE">Invoice</Option>
                  <Option value="QUOTE">Quote</Option>
                  <Option value="LEAD">Lead</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="trigger_type"
                label="Trigger Type"
                rules={[{ required: true, message: 'Please select trigger type' }]}
              >
                <Select placeholder="Select trigger type">
                  <Option value="PRE_DUE">Before Due Date</Option>
                  <Option value="DUE">On Due Date</Option>
                  <Option value="OVERDUE">After Due Date (Overdue)</Option>
                  <Option value="PRE_EXPIRY">Before Expiry</Option>
                  <Option value="EXPIRED">After Expiry</Option>
                  <Option value="IMMEDIATE">Immediate</Option>
                  <Option value="FOLLOW_UP">Follow-up</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="trigger_days"
                label="Trigger Days"
                rules={[{ required: true, message: 'Please enter trigger days' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Negative = before, Positive = after, 0 = on date"
                />
              </Form.Item>

              <Form.Item name="trigger_time" label="Trigger Time">
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </div>

            <Form.Item label="Channels">
              <Space>
                <Form.Item name="send_email" valuePropName="checked" noStyle>
                  <Checkbox>Email</Checkbox>
                </Form.Item>
                <Form.Item name="send_whatsapp" valuePropName="checked" noStyle>
                  <Checkbox>WhatsApp</Checkbox>
                </Form.Item>
                <Form.Item name="send_sms" valuePropName="checked" noStyle>
                  <Checkbox>SMS</Checkbox>
                </Form.Item>
              </Space>
            </Form.Item>

            <Form.Item label="Recipients">
              <Space>
                <Form.Item name="notify_customer" valuePropName="checked" noStyle initialValue={true}>
                  <Checkbox>Customer</Checkbox>
                </Form.Item>
                <Form.Item name="notify_admin" valuePropName="checked" noStyle>
                  <Checkbox>Admin</Checkbox>
                </Form.Item>
                <Form.Item name="notify_sales_rep" valuePropName="checked" noStyle>
                  <Checkbox>Sales Rep</Checkbox>
                </Form.Item>
              </Space>
            </Form.Item>

            <Form.Item
              name="email_subject"
              label="Email Subject (Optional)"
              tooltip="Custom subject line for emails. You can use template variables like {{customer_name}}, {{invoice_number}}, etc."
            >
              <Input placeholder="e.g., Payment Reminder for Invoice {{invoice_number}}" />
            </Form.Item>

            <Form.Item
              name="email_template"
              label="Email Template (Optional)"
            >
              <Select
                placeholder="Select email template"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {emailTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.send_whatsapp !== currentValues.send_whatsapp}
            >
              {({ getFieldValue }) =>
                getFieldValue('send_whatsapp') ? (
                  <Form.Item
                    name="whatsapp_template"
                    label="WhatsApp Template"
                    rules={[{ required: getFieldValue('send_whatsapp'), message: 'Please select a WhatsApp template' }]}
                  >
                    <Select
                      placeholder="Select WhatsApp template"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.props?.children?.[0]?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {whatsappTemplates.map(template => (
                        <Option key={template.id} value={template.id}>
                          <Space>
                            {template.name}
                            <Tag color="blue">{template.template_language}</Tag>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default ReminderRulesTable;
